import { Anchor, Divider, Group, List, Stack, Text, Title } from '@mantine/core';
import { Link } from 'react-router';
import { ISLERIM_HEDEF_HARITASI } from '@isg/validation';
import {
  islerimSayaci,
  useBenimGorevlerim,
  useIslerim,
  useKapanisOnayimBekleyenler,
  useKatilimciKararBekleyenler,
  useKendiSaglikKaydim,
} from '../api/queries';
import { ISLERIM_ETIKET } from '../i18n/tr';
import { BanaAtananlarScreen } from './BanaAtananlarScreen';
import { BekleyenOnaylarScreen } from './BekleyenOnaylarScreen';
import { AksiyonTablosu } from './aksiyon-parts';
import { BosListeNotu, QueryStatus } from './parts';
import { Rozet } from '../sozlesme';
import { tarihGoster } from '../lib/format';

/**
 * İŞLERİM — kişisel kuyruk (F4·S5·B1 — T1b).
 *
 * **MENÜDE SATIRI YOKTUR** ve bu bilinçlidir: menü ANLATIM SIRASINI izler
 * (kuruluş → çalışan → operasyon) ve günlük iş menünün altına düşer; T1'in
 * bu bedeli üst bardaki sayaçlı girişle + dashboard bloğuyla karşılanır.
 *
 * ⚠️ **MENÜSÜZ SAYFANIN DÖNÜŞ YOLU KENDİ ÜZERİNDEDİR** (IA §3/7): menüde
 * satırı olmadığı için kullanıcı buradan menüyle çıkamaz — geri dönüş
 * bağlantısı ekranın kendisinde olmak zorundadır, yoksa sıkışır.
 *
 * ✏️ **BULGU 103 (K-103a/b):** her kuyruk satırı KAYDINA gider ve yolu
 * `@isg/validation ISLERIM_HEDEF_HARITASI`ndan okur — bu ekran rota dizgesi
 * yazmaz (kod denetimi `baglanti-paketi.spec`).
 *
 * DÖRT kuyruk tek ekranda (Bana atananlar + Kapanış onayım bekleyenler +
 * Bekleyen onaylarım + Periyodik görevlerim) — dört ayrı menü satırı
 * doğurmadan.
 *
 * ⚠️ **SAYAÇ NE SAYIYORSA SAYFA ONU GÖSTERİR (0d — §2.40).** `useIslerim`
 * ÜÇ kuyruğu sayıyordu ama bu ekran YALNIZ İKİSİNİ çiziyordu: periyodik
 * görevi olan kullanıcı *"Açık işiniz: 2"* okuyup tek satır görüyor ve
 * kayıp işi ARAYAMIYORDU (sayacın kendisi doğruydu — eksik olan bölümdü).
 * Yeni bir kuyruk `useIslerim`e eklenirse buraya da bölümü eklenir.
 */
export function IslerimScreen(): React.JSX.Element {
  const islerim = useIslerim();

  return (
    <Stack>
      <Anchor component={Link} to="/" size="sm" data-testid="islerim-geri">
        ← Genel bakışa dön
      </Anchor>
      <Title order={3}>{ISLERIM_ETIKET}</Title>
      <Text size="sm" c="dimmed" data-testid="islerim-toplam">
        Açık işiniz: {islerimSayaci(islerim)}
      </Text>
      <Divider />
      <BanaAtananlarScreen />
      <Divider />
      <KapanisOnayimBolumu />
      <Divider />
      <BekleyenOnaylarScreen />
      <Divider />
      <KatilimciKararlariBolumu />
      <Divider />
      <GorevlerimBolumu />
      <Divider />
      <SaglikKaydimBolumu />
    </Stack>
  );
}

/**
 * ✏️ K-98 (bulgu 98) — AKTİF İZNE EKLENEN KATILIMCI KARARLARI. `useIslerim`in
 * beşinci kolu ile AYNI sorgu; karar izin sayfasında verilir (satır oraya
 * götürür — karar kutusu tek yerde).
 */
function KatilimciKararlariBolumu(): React.JSX.Element {
  const bekleyenler = useKatilimciKararBekleyenler();
  return (
    <Stack gap="xs">
      <Title order={4}>Katılımcı onayı bekleyenler</Title>
      <QueryStatus queries={[bekleyenler]} />
      {bekleyenler.isSuccess && bekleyenler.data.length === 0 && (
        <BosListeNotu mesaj="Kararınızı bekleyen katılımcı yok." />
      )}
      {bekleyenler.isSuccess && bekleyenler.data.length > 0 && (
        <List size="sm" data-testid="islerim-katilimci-kararlari">
          {bekleyenler.data.map((k) => (
            <List.Item key={k.participantId} data-testid="islerim-katilimci-karari">
              <Anchor
                component={Link}
                to={ISLERIM_HEDEF_HARITASI.katilimci(k.permit.id)}
                size="sm"
                data-testid="islerim-katilimci-bagi"
              >
                {k.personAd} ({k.gorevAd}) — {k.permit.workplaceAd} · {k.permit.locationAd}
              </Anchor>
            </List.Item>
          ))}
        </List>
      )}
    </Stack>
  );
}

/**
 * T9 — KAPANIŞ ONAYIM BEKLEYENLER. `Bana atananlar`ın aynası: orada
 * *"yapacağım işler"*, burada *"onaylayacağım işler"*.
 *
 * ⚠️ Sorumlu tamamladığını bildirdikten sonra iş ATAYANDA bekliyordu ve
 * atayanın bunu göreceği hiçbir yer yoktu — aksiyon listesinde diğer her
 * şeyin arasında kayboluyordu.
 */
function KapanisOnayimBolumu(): React.JSX.Element {
  const bekleyenler = useKapanisOnayimBekleyenler();
  return (
    <Stack gap="xs">
      <Title order={4}>Kapanış onayım bekleyenler</Title>
      <QueryStatus queries={[bekleyenler]} />
      {bekleyenler.isSuccess && bekleyenler.data.length === 0 && (
        <BosListeNotu mesaj="Onayınızı bekleyen aksiyon yok." />
      )}
      {bekleyenler.isSuccess && bekleyenler.data.length > 0 && (
        <AksiyonTablosu rows={bekleyenler.data} testId="kapanis-onayim-listesi" />
      )}
    </Stack>
  );
}

/**
 * PERİYODİK GÖREVLERİM (T9 kuyruğunun ekran karşılığı — 0d düzeltmesi).
 * `useIslerim`in üçüncü kolu ile AYNI sorgudan okur (`useBenimGorevlerim`) —
 * ikinci bir sayım/sorgu yolu açılmaz (IA §3/2).
 */
function GorevlerimBolumu(): React.JSX.Element {
  const gorevler = useBenimGorevlerim();
  return (
    <Stack gap="xs">
      <Title order={4}>Periyodik görevlerim</Title>
      <QueryStatus queries={[gorevler]} />
      {gorevler.isSuccess && gorevler.data.length === 0 && (
        <BosListeNotu mesaj="Üzerinizde bekleyen periyodik görev yok." />
      )}
      {gorevler.isSuccess && gorevler.data.length > 0 && (
        <List size="sm" data-testid="islerim-gorevlerim">
          {gorevler.data.map((gorev) => (
            <List.Item key={gorev.id} data-testid="islerim-gorev-kalemi">
              <Group gap="xs">
                <Anchor
                  component={Link}
                  to={ISLERIM_HEDEF_HARITASI.gorev(gorev.id)}
                  size="sm"
                  data-testid="islerim-gorev-bagi"
                >
                  {gorev.nesneAd} — {gorev.kalemAd}
                </Anchor>
                <Text size="xs" c="dimmed">
                  {tarihGoster(gorev.vadeTarihi)}
                </Text>
                {gorev.vadesiGecti && <Rozet anlam="gecikmis">Vadesi geçti</Rozet>}
              </Group>
            </List.Item>
          ))}
        </List>
      )}
    </Stack>
  );
}

/**
 * SAĞLIK KAYDIM — **madde 10'un DAR açılışının EKRANI** (tur 7 / U1;
 * §16.11/10).
 *
 * ⚠️ **UÇ VARDI, YOL YOKTU (15 gün).** F4·S1'de kişiye sağlık vade
 * bildirimi gitmeye başladı ve `GET health-opinions/kendi/:personId`
 * *"kişiye bildirim gidip ekranın 403 vermesi sistemin kendi kendine yalan
 * söylediği bir yüzey açardı"* gerekçesiyle yazıldı — **ama ekran hiç
 * yazılmadı**: bildirim gidiyor, tıklanacak bir yer yoktu.
 *
 * ⚠️ **GÖSTERİLEN YALNIZ İKİ TARİHTİR** ve bu bir ekran tercihi DEĞİL, API
 * sözleşmesidir: karar · kısıt · hekim · tetkik işaretleri yanıtta **hiç
 * yoktur**. Buraya "biraz daha bilgi" eklemek dar okuma listesini (§16.11/10)
 * delerdi.
 *
 * Kişi dosyasına bağlı olmayan hesapta (bekleyenler · temsilci) uç 403 döner
 * ve bölüm **çizilmez** — çalışmayacak bir kontrolü göstermeyiz (§2.40/2).
 */
function SaglikKaydimBolumu(): React.JSX.Element | null {
  const kaydim = useKendiSaglikKaydim();
  if (kaydim.isError) return null;
  return (
    <Stack gap="xs" data-testid="saglik-kaydim">
      <Title order={5}>Sağlık kaydım</Title>
      {kaydim.isSuccess && kaydim.data.kayit === null && (
        <BosListeNotu mesaj="Kayıtlı sağlık kanaatiniz görünmüyor." />
      )}
      {kaydim.data?.kayit && (
        <Group gap="lg">
          <Text size="sm" data-testid="saglik-kaydim-muayene">
            Muayene tarihi: {tarihGoster(kaydim.data.kayit.muayeneTarihi)}
          </Text>
          <Text size="sm" data-testid="saglik-kaydim-vade">
            Geçerlilik bitişi: {tarihGoster(kaydim.data.kayit.gecerlilikBitis)}
          </Text>
        </Group>
      )}
    </Stack>
  );
}
